import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Descriptions, Divider, Skeleton } from 'antd';

import { useDisclosure, useRouteMatchParam } from '@/hooks';

import {
  useBreadcrumbs,
  useUpdateBreadcrumbParam,
} from '@/contexts/BreadcrumbContextProvider';

import { usePart } from '@/core/domain/operational/Parts/Parts.hooks';

import { PageProps } from '@/types/PageProps';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import {
  useCreatePartVehicleApplication,
  useCreatePartVehicleApplicationFree,
} from '@/core/domain/operational/PartVehicleApplications/PartVehicleApplications.hooks';
import {
  CreatePartVehicleApplicationFreeFormValues,
  CreatePartVehicleApplicationFreeRequest,
  CreatePartVehicleApplicationRequest,
} from '@/core/domain/operational/PartVehicleApplications/PartVehicleApplications.types';
import { PartApplicationFormValues } from '@/components/Operational/Inventory/Parts/PartApplication/PartApplicationRecordsCreateModal/PartApplicantionRecordsModalForm';
import {
  useBrandMutation,
  useModelMutation,
} from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.hooks';
import { useEffect } from 'react';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import PartApplicationRecordsList from '@/components/Operational/Inventory/Parts/PartApplication/PartApplicationRecordsList';
import PartApplicationRecordsCreateModal from '@/components/Operational/Inventory/Parts/PartApplication/PartApplicationRecordsCreateModal';
import PartApplicationFreeCreateModal from '@/components/Operational/Inventory/Parts/PartApplication/PartApplicationFreeCreateModal';
import PartApplicationFreeList from '@/components/Operational/Inventory/Parts/PartApplication/PartApplicationFreeList';
import PartFooter from '@/components/Operational/Inventory/Parts/PartFooter';
import { useInventoryPartFormContext } from './../../InventoryPartsProvider/index';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import AccessDenied from '@/components/_common/AccessDenied';

function PartApplicationPage({ route }: PageProps) {
  const { data: matrixSettings } = useMatrix();
  const intl = useIntl();
  const partId = +useRouteMatchParam('partId');
  const { setParamLabel } = useBreadcrumbs();
  const [modalCreateRecordsVisible, modalCreateRecordsVisibleAction] =
    useDisclosure();
  const [modalCreateFreeVisible, modalCreateFreeVisibleAction] =
    useDisclosure();
  const { disabled } = useInventoryPartFormContext();

  const { data: part, isFetching: isFetchingPart } = usePart(+partId);

  useUpdateBreadcrumbParam(':partId', part?.name);

  const handleModalCreateRecordsVisible = () => {
    modalCreateRecordsVisibleAction.toggle();
  };

  const handleModalCreateFreeVisible = () => {
    modalCreateFreeVisibleAction.toggle();
  };

  useEffect(() => {
    if (part) {
      setParamLabel(':partId', part.name);
    }
  }, [part, isFetchingPart]);

  const createMutation = useCreatePartVehicleApplication();
  const brandMutation = useBrandMutation();
  const modelMutation = useModelMutation();

  const createApplicationFreeMutation = useCreatePartVehicleApplicationFree();

  const handleOkModalApplicationRecordsCreate = async (
    values: PartApplicationFormValues,
  ) => {
    try {
      if (values) {
        const brand = await brandMutation.mutateAsync(values.brandId);
        const model = await modelMutation.mutateAsync(values.modelId);

        const obj: CreatePartVehicleApplicationRequest = {
          toYear: Number(values.toYear),
          fromYear: Number(values.fromYear),
          model: {
            brand: {
              brandId: values.brandId,
              name: brand.name,
            },
            modelId: values.modelId,
            name: model.name,
          },
          partId: Number(partId),
        };

        await createMutation.mutateAsync(obj);

        handleModalCreateRecordsVisible();
      }
    } catch {}
  };

  const handleOkModalApplicationFreeCreate = async (
    values: CreatePartVehicleApplicationFreeFormValues,
  ) => {
    try {
      if (values) {
        const obj: CreatePartVehicleApplicationFreeRequest = {
          ...values,
          toYear: Number(values.toYear),
          fromYear: Number(values.fromYear),
          partId: Number(partId),
        };

        await createApplicationFreeMutation.mutateAsync(obj);

        handleModalCreateFreeVisible();
      }
    } catch {}
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        baseKey={partId.toString()}
        basePath={`/app/operational/inventory/parts/${partId}`}
        extra={
          <Button
            block
            type="primary"
            disabled={
              !hasPermissions([
                Authorities.REGISTER_PART_EDIT,
                Authorities.PART_APPLICATION_CREATE,
              ])
            }
            onClick={
              matrixSettings?.matrixSettings.defaultPartApplication
                ? handleModalCreateRecordsVisible
                : handleModalCreateFreeVisible
            }
          >
            <FormattedMessage id="commons.entity.add" />
          </Button>
        }
      >
        {!hasPermissions([Authorities.PART_APPLICATION_READ]) && (
          <AccessDenied />
        )}

        {hasPermissions([Authorities.PART_APPLICATION_READ]) && (
          <>
            <Skeleton active loading={isFetchingPart}>
              <Descriptions
                labelStyle={{ fontWeight: 'bold' }}
                column={{ xs: 1, md: 3, lg: 4, xxl: 4 }}
              >
                <Descriptions.Item
                  label={intl.formatMessage({ id: 'parts.entity.name' })}
                >
                  {part?.name}
                </Descriptions.Item>

                <Descriptions.Item
                  label={intl.formatMessage({
                    id: 'parts.entity.manufacturer',
                  })}
                >
                  {part?.manufacturer?.name || ''}
                </Descriptions.Item>

                <Descriptions.Item
                  label={intl.formatMessage({
                    id: 'parts.entity.subGroup',
                  })}
                >
                  {part?.subGroup?.name}
                </Descriptions.Item>
              </Descriptions>
            </Skeleton>

            <Divider />

            {matrixSettings?.matrixSettings.defaultPartApplication ? (
              <PartApplicationRecordsList partId={Number(partId)} />
            ) : (
              <PartApplicationFreeList partId={Number(partId)} />
            )}

            <PartFooter partId={Number(partId)} disabled={disabled} />
          </>
        )}

        <PartApplicationRecordsCreateModal
          onOk={handleOkModalApplicationRecordsCreate}
          visible={modalCreateRecordsVisible}
          onClose={handleModalCreateRecordsVisible}
          handleVisible={handleModalCreateRecordsVisible}
          isLoading={
            createMutation.isLoading ||
            brandMutation.isLoading ||
            modelMutation.isLoading
          }
        />

        <PartApplicationFreeCreateModal
          onOk={handleOkModalApplicationFreeCreate}
          visible={modalCreateFreeVisible}
          onClose={handleModalCreateFreeVisible}
          handleVisible={handleModalCreateFreeVisible}
          isLoading={createApplicationFreeMutation.isLoading}
        />
      </DefaultRouteTabs>
    </>
  );
}

export default PartApplicationPage;
